/* eslint-disable react/no-multi-comp */
import PropTypes from "prop-types";
import Radio from "app/pages/.shared/form/Radio";
import {
	CREDIT_CARDS_TYPES,
	MAX_PAYMENT_INSTALMENTS_WITH_FEES_DISPLAYED,
	PAYMENT_METHODS,
	PAYMENT_OPTIONS,
} from "app/constants";
import AmountContainer from "app/pages/.shared/AmountContainer";
import "./PaymentModesFieldset.scss";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import IconLogoAlma from "app/pages/.shared/static/icons/IconLogoAlma";
import { memo } from "react";
import { useFsFlag } from "@flagship.io/react-sdk";

const PaymentModesFieldset = ({
	total = 0,
	field = {},
	onChange,
	mensualite2Fois = {},
	paymentModes = [],
	casinoForbiddenCause,
	isDepositEnabled,
	isBookingEligibleForDeposit,
	isPaiementPledg4xAvailable,
	isPaymentAlmaAvailable,
	cardType,
}) => {
	const intl = useIntl();
	const show10XPaymentMethod = useFsFlag("available_payment_method_10x_show", true);

	const paymentDepositOptionSublabel = isBookingEligibleForDeposit
		? "payment.methods.deposit.label.simple.1"
		: "payment.methods.deposit.label.simple.1.b";

	return (
		<div className="payment-options__items">
			{paymentModes
				.filter(paymentMode => {
					if (paymentMode.terms === PAYMENT_OPTIONS["2x"] && isDepositEnabled) {
						return isBookingEligibleForDeposit;
					} else if (
						paymentMode?.terms === PAYMENT_OPTIONS["10x"] &&
						!show10XPaymentMethod.getValue()
					) {
						return false;
					}

					return true;
				})
				.map((paymentMode, index) => {
					const isPaymentCasino = paymentMode.type === PAYMENT_METHODS.CASINO;

					const isPaymentCasinoNotAllowedWithAmex =
						cardType === CREDIT_CARDS_TYPES.AMEX && isPaymentCasino;

					const isPaymentCasino4xNotAllowed = isPaymentCasino && casinoForbiddenCause;

					const radioValue = paymentMode.terms;
					const isPaymentModeselected =
						field.value === (radioValue && radioValue.toString());

					const paymentPartner = intl.formatMessage({
						id: `payment.method.${paymentMode.type}`,
					});

					const shouldDisplayNoFeesLabel =
						!paymentMode.instalments?.coutCredit &&
						paymentMode?.terms !== PAYMENT_OPTIONS["10x"];

					let headerComponent = (
						<div className="payment-options__line">
							<div>
								<div className="payment-options__line">
									<div
										className="payment-options__label payment-options__label--terms"
										data-testid="payment-options-label"
									>
										<FormattedMessage
											id="payment.methods.multiple.instlaments.label.simple"
											values={{
												terms: (
													<div className="payment-options__label-terms">
														<FormattedMessage
															id="payment.mode.terms.label"
															values={{
																terms: paymentMode.terms,
															}}
														/>
													</div>
												),
												sansFrais: shouldDisplayNoFeesLabel && (
													<FormattedMessage id="general.sans.frais" />
												),
												paymentType: isPaymentAlmaAvailable && (
													<div className="payment-options__label-alma">
														<FormattedMessage id="payment.methods.multiple.instlaments.paymentType.prefix" />
														<IconLogoAlma
															width={35}
															viewBox="0 10 35 15"
														/>
													</div>
												),
											}}
										/>
										{!isPaiementPledg4xAvailable &&
											!isPaymentAlmaAvailable &&
											"*"}
									</div>
								</div>
								<div className="payment-options__line">
									<div
										className="payment-options__sublabel payment-options__label--terms"
										data-testid="payment-options-sublabel"
									>
										{paymentMode.terms > PAYMENT_OPTIONS["4x"] ? (
											<FormattedMessage
												id="payment.methods.multiple.instlaments.label.partner"
												values={{
													terms: intl.formatMessage(
														{
															id: "general.payment.term.short",
														},
														{
															term: paymentMode.terms,
														}
													),
													paymentPartner,
												}}
											/>
										) : (
											<>
												<div className="payment-options__mensualites">
													(1 x{" "}
													<AmountContainer
														amount={paymentMode.instalments?.apport}
													/>
													) + ({paymentMode.terms - 1} x{" "}
													<AmountContainer
														amount={paymentMode.instalments?.mensualite}
													/>
													)
												</div>
												{paymentMode.instalments?.coutCredit > 0 && (
													<FormattedMessage
														id="payment.methods.multiple.instlaments.label.simple.2"
														values={{
															frais: (
																<AmountContainer
																	amount={
																		paymentMode.instalments
																			?.coutCredit
																	}
																/>
															),
														}}
													/>
												)}
												<FormattedMessage
													id="payment.methods.multiple.instlaments.sublabel"
													values={{
														paymentPartner,
													}}
													tagName="div"
												/>
											</>
										)}
									</div>
								</div>
							</div>
							{paymentMode.terms <= MAX_PAYMENT_INSTALMENTS_WITH_FEES_DISPLAYED && (
								<div
									className="payment-options__pay-now-amount"
									data-testid="pay-now-amount"
								>
									<AmountContainer amount={paymentMode.instalments?.apport} />
								</div>
							)}
						</div>
					);

					if (paymentMode.terms === PAYMENT_OPTIONS["1x"]) {
						headerComponent = (
							<div className="payment-options__line">
								<div
									className="payment-options__label"
									data-testid="payment-options-label"
								>
									<FormattedMessage id="payment.methods.full.label.simple" />
								</div>
								<div
									className="payment-options__pay-now-amount"
									data-testid="pay-now-amount"
								>
									<AmountContainer amount={total} />
								</div>
							</div>
						);
					} else if (paymentMode.terms === PAYMENT_OPTIONS["2x"]) {
						headerComponent = (
							<div className="payment-options__line">
								<div>
									<div className="payment-options__line">
										<div
											className="payment-options__label payment-options__label--terms"
											data-testid="payment-options-label"
										>
											{isBookingEligibleForDeposit ? (
												<div className="payment-options__deposit">
													<FormattedMessage id="payment.methods.multiple.instlaments.label.deposit" />

													<div className="payment-options__deposit-label">
														<FormattedMessage id="payment.methods.deposit.label" />
													</div>
												</div>
											) : (
												<FormattedMessage
													id="payment.methods.multiple.instlaments.label.simple"
													values={{
														terms: (
															<div className="payment-options__label-terms">
																<FormattedMessage
																	id="payment.mode.terms.label"
																	values={{
																		terms: 2,
																	}}
																/>
															</div>
														),
														sansFrais: (
															<FormattedMessage id="general.sans.frais" />
														),
														paymentType: false,
													}}
												/>
											)}
										</div>
									</div>
									<div className="payment-options__line">
										<div
											className="payment-options__sublabel"
											data-testid="payment-options-sublabel"
										>
											<FormattedMessage
												id={
													isBookingEligibleForDeposit
														? paymentDepositOptionSublabel
														: "payment.methods.partial.label.simple.1"
												}
												tagName="div"
												values={{
													percentage: mensualite2Fois.percentagePayNow,
													amountToPayNow: (
														<AmountContainer
															amount={mensualite2Fois.payNowAmount}
														/>
													),
												}}
											/>
											<FormattedMessage
												id={"payment.methods.partial.label.simple.2"}
												tagName="div"
												values={{
													solde: (
														<AmountContainer
															amount={mensualite2Fois.payLaterAmount}
														/>
													),
													date: (
														<span className="payment-options__date">
															<FormattedDate
																timeZone="UTC"
																day="2-digit"
																month="2-digit"
																year="numeric"
																value={mensualite2Fois.dueDate}
															/>
														</span>
													),
												}}
											/>
										</div>
									</div>
								</div>
								<div
									className="payment-options__pay-now-amount"
									data-testid="pay-now-amount"
								>
									<AmountContainer amount={mensualite2Fois.payNowAmount} />
								</div>
							</div>
						);
					}

					return (
						<div className="payment-options__item" key={index}>
							{isPaymentCasino4xNotAllowed && (
								<div
									className="payment-options__note"
									data-testid="payment-options-note"
								>
									{casinoForbiddenCause}
								</div>
							)}

							{isPaymentCasinoNotAllowedWithAmex && (
								<div
									className="payment-options__note"
									data-testid="payment-options-note"
								>
									<FormattedMessage id="error.payment.external.amex.not.supported" />
								</div>
							)}

							<Radio
								field={field}
								showIcon={true}
								disabled={
									isPaymentCasino4xNotAllowed || isPaymentCasinoNotAllowedWithAmex
								}
								name={`payment-mode-${radioValue}`}
								id={`payment-mode-${radioValue}`}
								value={radioValue}
								checked={isPaymentModeselected}
								toggle={() => {
									onChange(radioValue);
								}}
								data-testid={`payment-mode-${radioValue}`}
							>
								<div className="payment-options__content">{headerComponent}</div>
							</Radio>
						</div>
					);
				})}
		</div>
	);
};

PaymentModesFieldset.propTypes = {
	cardType: PropTypes.string,
	paymentModes: PropTypes.arrayOf(PropTypes.object),
	total: PropTypes.number,
	field: PropTypes.object,
	onChange: PropTypes.func,
	casinoForbiddenCause: PropTypes.string,
	mensualite2Fois: PropTypes.shape({
		percentagePayNow: PropTypes.number,
		payLaterAmount: PropTypes.number,
		payNowAmount: PropTypes.number,
		dueDate: PropTypes.number,
	}),
	isDepositEnabled: PropTypes.bool,
	isBookingEligibleForDeposit: PropTypes.bool,
	isPaiementPledg4xAvailable: PropTypes.bool,
	isPaymentAlmaAvailable: PropTypes.bool,
};

export default memo(PaymentModesFieldset);
